import DatePicker from 'react-datepicker';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import isWithinInterval from 'date-fns/isWithinInterval';
import parseISO from 'date-fns/parseISO';
import set from 'date-fns/set';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import 'react-datepicker/dist/react-datepicker.css';
import './EffectiveDateSelector.css';

type EffectiveDateSelectorProps = {
  baseDate: Date;
  effectiveDate: Date;
  onChange: (date: Date) => void;
  billingPeriods?: BillingPeriod[];
  currentBillingPeriod?: BillingPeriod;
  disabled?: boolean;
  maxDaysInPast?: number;
};

export default function EffectiveDateSelector({
  baseDate,
  effectiveDate,
  onChange,
  billingPeriods,
  currentBillingPeriod,
  disabled = false,
  maxDaysInPast = 10000,
}: EffectiveDateSelectorProps) {
  const daysInPast = Math.max(0, differenceInCalendarDays(startOfDay(baseDate), startOfDay(effectiveDate)));
  const isInBillingPeriod = (date: Date, billingPeriod: BillingPeriod) =>
    isWithinInterval(startOfDay(date), {
      start: startOfDay(parseISO(billingPeriod.startDate)),
      end: startOfDay(parseISO(billingPeriod.endDate)),
    });
  const getBillingPeriod = (date: Date) => {
    if (currentBillingPeriod && isInBillingPeriod(date, currentBillingPeriod)) {
      return currentBillingPeriod;
    }

    return billingPeriods?.find((billingPeriod) => isInBillingPeriod(date, billingPeriod));
  };
  const selectedBillingPeriod = getBillingPeriod(effectiveDate);

  const updateDaysInPast = (value: string) => {
    const days = Number(value);
    if (!Number.isInteger(days) || days < 0 || days > maxDaysInPast) {
      return;
    }

    onChange(subDays(baseDate, days));
  };

  const updateEffectiveDate = (date: Date | null) => {
    if (!date) {
      return;
    }

    onChange(
      set(baseDate, {
        year: date.getFullYear(),
        month: date.getMonth(),
        date: date.getDate(),
      })
    );
  };

  return (
    <>
      <div className="field">
        <label>Days in Past</label>
        <input
          type="number"
          min="0"
          max={maxDaysInPast}
          value={daysInPast}
          onChange={(event) => updateDaysInPast(event.target.value)}
          disabled={disabled}
        />
      </div>
      <div className="field effective-date-selector">
        <label>Effective Date</label>
        <DatePicker
          className="effective-date-selector__input"
          selected={effectiveDate}
          onChange={updateEffectiveDate}
          dateFormat="yyyy-MM-dd"
          minDate={subDays(baseDate, maxDaysInPast)}
          maxDate={baseDate}
          disabled={disabled}
          calendarClassName="effective-date-selector__calendar"
          dayClassName={(date) => {
            const billingPeriod = getBillingPeriod(date);
            return billingPeriod
              ? `effective-date-selector__period-${billingPeriod.billingPeriodId % 2}`
              : undefined;
          }}
        />
        {selectedBillingPeriod && (
          <div className="effective-date-selector__period-message">
            * Date is in billing period {selectedBillingPeriod.billingPeriodId}
            {selectedBillingPeriod.billingPeriodId === currentBillingPeriod?.billingPeriodId
              ? ' (current billing period)'
              : ''}.
          </div>
        )}
      </div>
    </>
  );
}
